import { lazy, Suspense, useState } from "react";
import { motion } from "framer-motion";
import { Layers, Power, Move3d } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const Juicer3D = lazy(() => import("@/components/site/SlowpourModel"));

const INFO = {
  lid: ["Wide feed chute", "A large-diameter opening takes whole apples, oranges and carrots — far less chopping before you press."],
  hopper: ["Self-feeding hopper", "Load it up, close the lid and walk away. The hopper guides produce down to the auger on its own."],
  auger: ["Slow masticating auger", "Turns at low speed to crush and squeeze rather than shred, so juice stays cool, vibrant and low-foam."],
  screen: ["Fine-mesh screen", "Separates silky juice from dry pulp. Rinses clean in seconds under the tap."],
  chamber: ["Juicing bowl & spout", "Juice flows out the front spout into the included cup; pulp exits the side into its own bin."],
  base: ["Quiet motor base", "A weighted, BPA-free base with a simple dial control — quiet enough for early mornings."],
};
const ORDER = ["lid", "hopper", "auger", "screen", "chamber", "base"];

const Toggle = ({ on, onClick, icon: Icon, label, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    aria-pressed={on}
    className={`flex flex-1 items-center justify-between gap-3 rounded-2xl border px-5 py-4 text-left transition-[background-color,border-color] duration-300 ${
      on ? "border-[#E0A030] bg-[#E0A030]/15" : "border-white/10 bg-white/[0.03] hover:border-white/25"
    }`}
  >
    <span className="flex items-center gap-3 text-sm font-medium text-[#F2EAE1]"><Icon size={18} className="text-[#E0A030]" />{label}</span>
    <span className={`relative h-5 w-9 rounded-full transition-colors duration-300 ${on ? "bg-[#E0A030]" : "bg-white/15"}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-[#181615] transition-transform duration-300 ${on ? "translate-x-[18px]" : "translate-x-0.5"}`} />
    </span>
  </button>
);

export const Studio = () => {
  const [exploded, setExploded] = useState(false);
  const [running, setRunning] = useState(false);
  const [active, setActive] = useState(null);
  const sel = active ? INFO[active] : null;

  return (
    <section id="studio" data-testid="3d-juicer-container" className="relative overflow-hidden bg-[#181615] py-20 text-[#F2EAE1] sm:py-28">
      <div className="pointer-events-none absolute left-1/3 top-1/4 h-[480px] w-[480px] rounded-full bg-[#C85A32]/20 blur-[140px]" />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="mb-12 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <p className="eyebrow mb-4 text-[#E0A030]">The 3D Studio</p>
            <h2 className="font-display text-4xl font-light leading-[1] tracking-tight sm:text-5xl lg:text-6xl">
              Take it apart. <span className="italic text-[#E0A030]">Switch it on.</span>
            </h2>
          </div>
          <p className="max-w-md text-base text-[#C9BFB5] md:text-lg">Drag to rotate. Explode the build to see every component, or run the motor to watch the auger press.</p>
        </Reveal>

        <div className="grid gap-6 lg:grid-cols-12">
          <div className="relative h-[460px] overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-b from-[#24201E] to-[#181615] sm:h-[560px] lg:col-span-8 lg:h-[640px]">
            <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-[#C9BFB5]">Loading 3D studio…</div>}>
              <Juicer3D exploded={exploded} running={running} active={active} onSelect={(id) => setActive((a) => (a === id ? null : id))} />
            </Suspense>
            <span className="eyebrow pointer-events-none absolute left-5 top-5 flex items-center gap-2 text-[#C9BFB5]"><Move3d size={14} /> Drag to orbit</span>
            {running && <span data-testid="motor-running-indicator" className="eyebrow absolute right-5 top-5 flex items-center gap-2 text-[#E0A030]"><span className="pulse-dot h-2 w-2 rounded-full bg-[#E0A030]" /> Pressing</span>}
          </div>

          <div className="flex flex-col gap-4 lg:col-span-4">
            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <Toggle testId="toggle-exploded-view" on={exploded} onClick={() => setExploded((v) => !v)} icon={Layers} label={exploded ? "Exploded build" : "Assembled build"} />
              <Toggle testId="toggle-run-motor" on={running} onClick={() => setRunning((v) => !v)} icon={Power} label={running ? "Motor running" : "Run the motor"} />
            </div>
            <div className="rounded-[24px] border border-white/10 bg-white/[0.03] p-6">
              <p className="eyebrow mb-4 text-[#C9BFB5]">Components</p>
              <div className="flex flex-wrap gap-2">
                {ORDER.map((id) => (
                  <button
                    key={id}
                    data-testid={`part-chip-${id}`}
                    onClick={() => setActive((a) => (a === id ? null : id))}
                    className={`rounded-full border px-3.5 py-1.5 text-xs transition-[background-color,color,border-color] duration-300 ${
                      active === id ? "border-[#E0A030] bg-[#E0A030] text-[#181615]" : "border-white/15 text-[#F2EAE1] hover:border-white/40"
                    }`}
                  >
                    {INFO[id][0]}
                  </button>
                ))}
              </div>
              <motion.div key={active || "none"} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} data-testid="part-info-panel" className="mt-6 min-h-[120px]">
                {sel ? (
                  <>
                    <h3 className="font-display text-2xl italic">{sel[0]}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#C9BFB5]">{sel[1]}</p>
                  </>
                ) : (
                  <p className="text-sm leading-relaxed text-[#C9BFB5]">Tap a golden hotspot on the model — or a component above — to learn what each part does.</p>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
